import React, { useState, ReactElement } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import PasswordInput from '../halda-design-system/inputs/PasswordInput';
import { PasswordDetailsType } from '../settings/user/edit/EditUser';

interface PasswordVerificationProps {
  isInSettings: boolean;
  isRequired: boolean;
  submitButtonText: string;
  setPasswordDetails?: React.Dispatch<React.SetStateAction<PasswordDetailsType>>;
}

const MIN_LENGTH = 12;

const PasswordVerification = ({
  isInSettings, isRequired, submitButtonText, setPasswordDetails,
}: PasswordVerificationProps): ReactElement => {
  const [userPassword, setUserPassword] = useState<string>('');
  const [userPasswordConfirmation, setUserPasswordConfirmation] = useState<string>('');
  const [isMinLength, setIsMinLength] = useState<boolean>(false);
  const [isUpperAndLowerCase, setIsUpperAndLowerCase] = useState<boolean>(false);
  const [hasNumberAndSymbol, setHasNumberAndSymbol] = useState<boolean>(false);
  const [isMatching, setIsMatching] = useState<boolean>(false);
  const [hasFailedAttempt, setHasFailedAttempt] = useState<boolean>(false);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setUserPassword(password);

    setIsMatching(password === userPasswordConfirmation && password.length > 0);

    setIsMinLength(password.length >= MIN_LENGTH);

    const hasUpperAndLowerCase = new RegExp(/^(?=.*[a-z])(?=.*[A-Z])/).test(password);
    setIsUpperAndLowerCase(hasUpperAndLowerCase);

    const hasNumberAndSymbol = new RegExp(/^(?=.*\d)(?=.*\W)/).test(password);
    setHasNumberAndSymbol(hasNumberAndSymbol);

    if (setPasswordDetails) {
      setPasswordDetails((prev) => ({
        ...prev,
        password,
      }))
    }
  };

  const handlePasswordConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const passwordConfirmation = e.target.value;
    setUserPasswordConfirmation(passwordConfirmation);
    setIsMatching(passwordConfirmation === userPassword && passwordConfirmation.length > 0);
    if (setPasswordDetails) {
      setPasswordDetails((prev) => ({
        ...prev,
        password_confirmation: passwordConfirmation,
        is_valid: passwordConfirmation === userPassword && isMinLength && isUpperAndLowerCase && hasNumberAndSymbol,
      }))
    }
  };

  const submitOnClick = () => {
    const form = document.querySelector('form');
    if (!isRequired && userPassword.length === 0) {
      form?.requestSubmit();
      return;
    }
    if (isMinLength && isUpperAndLowerCase && hasNumberAndSymbol && isMatching) {
      setHasFailedAttempt(false);
      form?.requestSubmit();
    } else {
      setHasFailedAttempt(true);
    }
  };

  const handleKeyboardButton = (
    e: React.KeyboardEvent<HTMLInputElement>, isEmailInput: boolean,
  ) => {
    if (e.key === 'Enter') {
      submitOnClick();
    }
  };

  const IncorrectIcon = hasFailedAttempt ? (
    <XCircleIcon className="h-4 w-4 my-auto text-red-500" />
  ) : (
    <ExclamationCircleIcon className="h-4 w-4 my-auto text-yellow-300" />
  );

  const requirementTextColor = hasFailedAttempt ? 'text-red-500' : 'text-gray-500';

  const passwordValues = {
    id: isInSettings ? 'password' : 'user_password',
    name: isInSettings ? 'password' : 'user[password]',
  };
  const passwordConfirmationValues = {
    id: isInSettings ? 'password_confirmation' : 'user_password_confirmation',
    name: isInSettings ? 'password_confirmation' : 'user[password_confirmation]',
  };

  return (
    <>
      <div>
        <label
          htmlFor={passwordValues.id}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {`Password${isRequired ? '*' : ''}`}
        </label>
        <PasswordInput
          id={passwordValues.id}
          isRequired={isRequired}
          name={passwordValues.name}
          autoComplete="new-password"
          onChange={handlePasswordChange}
          handleKeyboardButton={handleKeyboardButton}
        />
      </div>
      <div className="pt-2">
        <label
          htmlFor={passwordConfirmationValues.id}
          className="block text-sm font-medium text-gray-700"
        >
          {`Confirm Password${isRequired ? '*' : ''}`}
        </label>
        <PasswordInput
          id={passwordConfirmationValues.id}
          isRequired={isRequired}
          name={passwordConfirmationValues.name}
          autoComplete="new-password"
          onChange={handlePasswordConfirmationChange}
          handleKeyboardButton={handleKeyboardButton}
        />

        <div className="pl-2 mt-2 space-y-1">
          <div className="flex flex-row">
            {isMinLength ? <CheckCircleIcon className="h-4 w-4 my-auto text-green-500" /> : IncorrectIcon}
            <span className={`ml-2 text-sm ${requirementTextColor}`}>
              Minimum of
              {' '}
              {MIN_LENGTH}
              {' '}
              characters
            </span>
          </div>
          <div className="flex flex-row">
            {isUpperAndLowerCase ? <CheckCircleIcon className="h-4 w-4 my-auto text-green-500" /> : IncorrectIcon}
            <span className={`ml-2 text-sm ${requirementTextColor}`}>Upper and lower case letters</span>
          </div>
          <div className="flex flex-row">
            {hasNumberAndSymbol ? <CheckCircleIcon className="h-4 w-4 my-auto text-green-500" /> : IncorrectIcon}
            <span className={`ml-2 text-sm ${requirementTextColor}`}>Must contain a number and a symbol</span>
          </div>
          <div className="flex flex-row">
            {isMatching ? <CheckCircleIcon className="h-4 w-4 my-auto text-green-500" /> : IncorrectIcon}
            <span className={`ml-2 text-sm ${requirementTextColor}`}>Password confirmation</span>
          </div>
        </div>
      </div>
      {!isInSettings && (
        <div>
          <button
            type="button"
            onClick={submitOnClick}
            className="bg-gradient-to-t from-blue-600 to-blue-500 cursor-pointer w-full h-14 flex justify-center py-2 px-4 border border-blue-600 rounded-md drop-shadow-xl text-md font-medium text-white focus:outline-none hover:bg-blue-500"
          >
            <span className="my-auto">{submitButtonText}</span>
          </button>
        </div>
      )}
    </>
  );
};

export default PasswordVerification;
